import {
  Alert,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { JiraAssetCandidate } from "@msc/shared";

import { useJiraAsset, useSetJiraAsset } from "../hooks/useJiraAsset.js";
import { describeApiError } from "../lib/errorText.js";

/**
 * Pflege der Verknüpfung Beauftragung ↔ Jira-Kundenabteilung (F1/F5).
 *
 * **Genau eine, und ein Mensch wählt sie.** Die Liste ist nach Ähnlichkeit zum
 * Kundennamen sortiert und verknüpft nichts von selbst; ein exakter
 * Namensvergleich, der stillschweigend danebengreift, hat in diesem Projekt
 * bereits Schaden angerichtet (Personio, 2026-08-06).
 *
 * **Was das Verknüpfen bedeutet, steht hier und nicht im Kleingedruckten:**
 * Name und Service Lead der Beauftragung kommen danach aus Jira, und der
 * Service Lead entscheidet über Sichtbarkeit im CRM (F3).
 *
 * Aufgeklappt statt immer sichtbar: Die Verknüpfung wird selten gepflegt, und
 * die Abfrage zieht alle Kundenabteilungen des Spiegels. Erst beim Aufklappen
 * zu laden hält die Kundenakte schnell.
 */
export function JiraAssetPanel({
  engagementId,
  customerId,
}: {
  engagementId: string;
  customerId: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeQuery, setActiveQuery] = useState("");

  const view = useJiraAsset(engagementId, activeQuery, open);
  const setAsset = useSetJiraAsset(engagementId, customerId);

  const current = view.data?.current ?? null;

  return (
    <Box>
      <Group gap="xs">
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "▾ " : "▸ "}
          {t("pages.customers.detail.jiraAsset")}
        </Button>
        {open && view.data && (
          <Badge size="sm" variant="light" color={current ? "teal" : "orange"}>
            {current
              ? (current.jiraAssetLabel ?? current.jiraAssetId)
              : t("pages.customers.detail.jiraAssetNone")}
          </Badge>
        )}
      </Group>

      <Collapse in={open}>
        <Stack gap={6} mt={6} pl="xs">
          {view.isLoading && <Loader size="xs" />}
          {view.isError && (
            <Text c="red" size="xs">
              {describeApiError(view.error, t)}
            </Text>
          )}

          {view.data && (
            <>
              {/* Was die fehlende Verknuepfung fuer den Bericht bedeutet, steht
                  hier — nicht erst im Bericht, wo es zu spaet auffaellt. */}
              {!current && (
                <Alert color="orange" p="xs" variant="light">
                  <Text size="xs">
                    {t("pages.customers.detail.jiraAssetNoneHint")}
                  </Text>
                </Alert>
              )}

              {view.data.mirrorEmpty && (
                <Alert color="gray" p="xs" variant="light">
                  <Text size="xs">
                    {t("pages.customers.detail.jiraAssetMirrorEmpty")}
                  </Text>
                </Alert>
              )}

              {current && (
                <Group gap="xs" wrap="wrap">
                  <Badge size="lg" variant="light" color="teal">
                    {current.jiraAssetLabel ?? current.jiraAssetId}
                    {current.jiraAssetKey ? ` (${current.jiraAssetKey})` : ""} —{" "}
                    {t("pages.customers.detail.jiraAssetIssues", {
                      count: current.issueCount,
                    })}
                  </Badge>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    loading={setAsset.isPending}
                    onClick={() => setAsset.mutate(null)}
                  >
                    {t("pages.customers.detail.jiraAssetUnlink")}
                  </Button>
                </Group>
              )}

              {/* Die Folge des Verknuepfens — bevor jemand klickt, nicht danach. */}
              <Text size="xs" c="dimmed">
                {t("pages.customers.detail.jiraAssetManagedHint")}
              </Text>

              {setAsset.isError && (
                <Text c="red" size="xs">
                  {describeApiError(setAsset.error, t)}
                </Text>
              )}

              {/* Freie Suche: der Ausweg, wenn der Klarname im Spiegel anders
                  geschrieben ist als der Kundenname im CRM. */}
              <Group gap="xs" mt={4}>
                <TextInput
                  size="xs"
                  placeholder={t(
                    "pages.customers.detail.jiraAssetSearchPlaceholder",
                  )}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setActiveQuery(search.trim());
                  }}
                  aria-label={t("pages.customers.detail.jiraAssetSearch")}
                  style={{ minWidth: 260 }}
                />
                <Button
                  size="compact-xs"
                  variant="light"
                  onClick={() => setActiveQuery(search.trim())}
                >
                  {t("pages.customers.detail.jiraAssetSearch")}
                </Button>
              </Group>

              <Stack gap={4}>
                {view.data.candidates.length === 0 && (
                  <Text size="xs" c="dimmed">
                    {t("pages.customers.detail.jiraAssetNoMatch")}
                  </Text>
                )}
                {view.data.candidates.map((c) => (
                  <CandidateRow
                    key={c.kundenabteilungId}
                    candidate={c}
                    linked={current?.jiraAssetId === c.kundenabteilungId}
                    pending={setAsset.isPending}
                    onPick={() => setAsset.mutate(c.kundenabteilungId)}
                  />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

function CandidateRow({
  candidate,
  linked,
  pending,
  onPick,
}: {
  candidate: JiraAssetCandidate;
  linked: boolean;
  pending: boolean;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const taken = candidate.takenByEngagementName !== null;

  return (
    <Group gap="xs" wrap="nowrap">
      <Button
        size="compact-xs"
        variant="light"
        disabled={pending || taken || linked}
        onClick={onPick}
      >
        {linked
          ? t("pages.customers.detail.jiraAssetLinked")
          : t("pages.customers.detail.jiraAssetAdopt")}
      </Button>
      <Text size="xs" style={{ flex: 1 }}>
        {candidate.kundenabteilungLabel ?? candidate.kundenabteilungId}{" "}
        <Text span c="dimmed" size="xs">
          (
          {t("pages.customers.detail.jiraAssetIssues", {
            count: candidate.issueCount,
          })}
          )
        </Text>
        {/* „Schon vergeben" wird gezeigt, nicht verschwiegen — sonst sucht
            der Bearbeiter die Abteilung, die er sieht, aber nicht bekommt. */}
        {taken && (
          <Text span c="orange" size="xs">
            {" "}
            —{" "}
            {t("pages.customers.detail.jiraAssetTaken", {
              name: candidate.takenByEngagementName,
            })}
          </Text>
        )}
      </Text>
    </Group>
  );
}
